import { useNavigate } from 'react-router-dom';
import { ArrowDown, ArrowUp, CreditCard, Pencil, Plus } from 'lucide-react';
import { Fab } from '@/components/ui/Fab';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import {
  fetchAdminPlans,
  toggleAdminPlan,
  updateAdminPlan,
  type PlanoAdmin,
} from '../../api/client';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { PageHeader } from '../../components/ui';
import { Spinner } from '../../components/ui/spinner';
import { Switch } from '../../components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import { useNotification } from '../../context/NotificationProvider';
import { classifyError, notifyMutationError } from '../../lib/errors';
import { formatPrice } from '../../lib/formatters';

export default function PlansManagementPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();

  const { data: plans, isLoading, error } = useQuery({
    queryKey: ['admin', 'plans'],
    queryFn: fetchAdminPlans,
  });

  const toggleMutation = useMutation({
    mutationFn: (id: string) => toggleAdminPlan(id),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'plans'] });
      showSuccess(`Plano ${updated.ativo ? 'ativado' : 'desativado'}`);
    },
    onError: (err) => notifyMutationError(err, showError),
  });

  const reorderMutation = useMutation({
    mutationFn: async ({ planA, planB }: { planA: PlanoAdmin; planB: PlanoAdmin }) => {
      await Promise.all([
        updateAdminPlan(planA.id, { ordem_exibicao: planB.ordem_exibicao }),
        updateAdminPlan(planB.id, { ordem_exibicao: planA.ordem_exibicao }),
      ]);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'plans'] });
    },
    onError: (err) => notifyMutationError(err, showError),
  });

  const sorted = [...(plans ?? [])].sort((a, b) => a.ordem_exibicao - b.ordem_exibicao);

  const handleMoveUp = (index: number) => {
    if (index <= 0) return;
    reorderMutation.mutate({ planA: sorted[index], planB: sorted[index - 1] });
  };

  const handleMoveDown = (index: number) => {
    if (index >= sorted.length - 1) return;
    reorderMutation.mutate({ planA: sorted[index], planB: sorted[index + 1] });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Gestão de Planos"
        subtitle="Gerencie planos de assinatura, preços, limites e ordem de exibição."
        icon={<CreditCard />}
        actions={
          <Button onClick={() => navigate('/app/admin/planos/novo')} className="hidden sm:inline-flex">
            <Plus className="mr-2 h-4 w-4" />
            Novo Plano
          </Button>
        }
      />

      <Fab icon={<Plus className="h-6 w-6" />} label="Novo Plano" onClick={() => navigate('/app/admin/planos/novo')} />

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{classifyError(error).message}</AlertDescription>
        </Alert>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner size={32} label="Carregando planos" />
        </div>
      ) : sorted.length === 0 ? (
        <Card className="p-8 text-center text-muted-foreground">
          Nenhum plano cadastrado. Clique em &quot;Novo Plano&quot; para começar.
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-20 text-center">Ordem</TableHead>
                <TableHead>Plano</TableHead>
                <TableHead className="hidden sm:table-cell">Grupo</TableHead>
                <TableHead className="hidden md:table-cell">Preço</TableHead>
                <TableHead className="hidden lg:table-cell">Trial</TableHead>
                <TableHead className="text-center">Ativo</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((plan, index) => (
                <TableRow key={plan.id} className={!plan.ativo ? 'opacity-50' : ''}>
                  <TableCell className="text-center">
                    <div className="flex items-center justify-center gap-0.5">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => handleMoveUp(index)}
                            disabled={index === 0 || reorderMutation.isPending}
                            aria-label={`Mover ${plan.nome} para cima`}
                          >
                            <ArrowUp size={14} />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Subir</TooltipContent>
                      </Tooltip>
                      <span className="min-w-5 text-center text-xs text-muted-foreground">
                        {plan.ordem_exibicao}
                      </span>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => handleMoveDown(index)}
                            disabled={index === sorted.length - 1 || reorderMutation.isPending}
                            aria-label={`Mover ${plan.nome} para baixo`}
                          >
                            <ArrowDown size={14} />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Descer</TooltipContent>
                      </Tooltip>
                    </div>
                  </TableCell>

                  <TableCell>
                    <div className="flex flex-col gap-0.5">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{plan.nome}</span>
                        {plan.destaque && <Badge variant="default" className="text-[11px] px-1.5 py-0">Destaque</Badge>}
                      </div>
                      <code className="text-xs text-muted-foreground">{plan.codigo}</code>
                      <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground sm:hidden">
                        <span>{plan.grupo}</span>
                        <span>{formatPrice(plan.preco_centavos)}/{plan.intervalo === 'ANUAL' ? 'ano' : 'mês'}</span>
                      </div>
                    </div>
                  </TableCell>

                  <TableCell className="hidden sm:table-cell">
                    <span className="text-sm capitalize">{plan.grupo}</span>
                  </TableCell>

                  <TableCell className="hidden md:table-cell">
                    <span className="text-sm">
                      {formatPrice(plan.preco_centavos)}
                      <span className="text-muted-foreground"> / {plan.intervalo === 'ANUAL' ? 'ano' : 'mês'}</span>
                    </span>
                  </TableCell>

                  <TableCell className="hidden lg:table-cell">
                    <span className="text-sm">{plan.trial_dias ? `${plan.trial_dias} dias` : '—'}</span>
                  </TableCell>

                  <TableCell className="text-center">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="inline-flex justify-center">
                          <Switch
                            checked={plan.ativo}
                            onCheckedChange={() => toggleMutation.mutate(plan.id)}
                            disabled={toggleMutation.isPending}
                            aria-label={plan.ativo ? 'Desativar plano' : 'Ativar plano'}
                            className="scale-75"
                          />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>{plan.ativo ? 'Desativar' : 'Ativar'}</TooltipContent>
                    </Tooltip>
                  </TableCell>

                  <TableCell className="text-right">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => navigate(`/app/admin/planos/${plan.id}`)}
                        >
                          <Pencil size={14} />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Editar plano</TooltipContent>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
